import {
  ModernNotification,
  createNotification,
  createSyncNotification,
  createOrderNotification,
  NotificationType,
  NotificationKind,
  DEFAULT_KIND_BY_TYPE,
  NOTIFICATION_KIND_CONFIG,
  mapLegacyToNotificationType,
} from '../types/ModernNotification';
import { env } from '../../../../config/env';
import { LCName, getItemLC, setItemLC } from '../../localstorage';
import Notification from '../../../models/Notification';
import { notificationRepository, NotificationQueryOptions } from '../repositories/NotificationRepository';

export class NotificationService {
  private static instance: NotificationService;
  private currentUserId: string | null = null;

  constructor() {
    // Get current user ID from localStorage
    this.currentUserId = this.detectUserId();
  }

  static getInstance(): NotificationService {
    if (!this.instance) {
      this.instance = new NotificationService();
    }
    return this.instance;
  }

  private detectUserId(): string | null {
    // Try multiple sources for user ID
    
    // Method 1: Try UserData first (current approach)
    const userData = getItemLC(LCName.UserData);
    if (userData?.id) {
      console.log('NotificationService: Found user ID in UserData:', userData.id);
      return userData.id;
    }

    // Method 2: Try User object with uid
    const user = getItemLC(LCName.User);
    if (user?.uid) {
      console.log('NotificationService: Found user ID in User.uid:', user.uid);
      return user.uid;
    }

    // Method 3: Try direct UserID key
    const directUserId = getItemLC(LCName.UserID);
    if (directUserId) {
      console.log('NotificationService: Found user ID in UserID key:', directUserId);
      return directUserId;
    }

    // Method 4: Try the UID key
    const uidKey = getItemLC('UID');
    if (uidKey) {
      console.log('NotificationService: Found user ID in UID key:', uidKey);
      return uidKey;
    }

    console.warn('NotificationService: No user ID found in any localStorage keys');
    console.log('Available localStorage data:', {
      UserData: userData,
      User: user,
      UserID: directUserId,
      UID: uidKey
    });

    return null;
  }

  private getUserId(): string | null {
    if (!this.currentUserId) {
      this.currentUserId = this.detectUserId();
    }
    return this.currentUserId;
  }

  // Convert legacy notification to modern format
  convertLegacyNotification(legacyNotif: Notification): ModernNotification {
    // Map legacy type to new notification type
    const notifType = mapLegacyToNotificationType(
      legacyNotif.notifType || 'SYSTEM',
      legacyNotif.status
    );
    const kind = DEFAULT_KIND_BY_TYPE[notifType];
    const kindConfig = NOTIFICATION_KIND_CONFIG[kind];

    const baseNotif = {
      id: legacyNotif.id || Date.now().toString(),
      timestamp: legacyNotif.createdAt ? new Date(legacyNotif.createdAt.seconds * 1000) : new Date(),
      read: legacyNotif.read || false,
      collaborationId: legacyNotif.collaborationID,
      phoneNumber: legacyNotif.phoneNumber,
      customerId: legacyNotif.customerID,
      companyId: legacyNotif.companyID,
      companyName: legacyNotif.companyName,
      roomId: legacyNotif.roomID,
    };

    switch (legacyNotif.notifType) {
      case 'SyncOrder':
        return {
          ...baseNotif,
          type: 'SYNC_COMPLETED' as NotificationType,
          kind: 'success' as NotificationKind,
          title: 'Sync Completed',
          message: `Products from '${legacyNotif.companyName || 'store'}' have been synced.`,
          icon: 'sync',
          iconColor: 'tw-text-green-500',
          actionUrl: '/orders',
          actionLabel: 'View Orders'
        };

      case 'newMessage':
      case 'CIQMessage':
      case 'newUserMessage':
        return {
          ...baseNotif,
          type: 'NEW_MESSAGE' as NotificationType,
          kind: 'message' as NotificationKind,
          title: 'New Message',
          message: `New message from ${legacyNotif.name || legacyNotif.profileName || legacyNotif.phoneNumber}`,
          icon: 'chat',
          iconColor: 'tw-text-blue-500',
          actionUrl: '/chat',
          actionLabel: 'Reply',
          senderName: legacyNotif.name || legacyNotif.profileName,
        };

      default:
        return {
          ...baseNotif,
          type: notifType,
          kind,
          title: 'Notification',
          message: legacyNotif.textContent || 'You have a new notification',
          icon: kindConfig.icon,
          iconColor: kindConfig.iconColor
        };
    }
  }

  // Check if we should show sample data (development mode only)
  private shouldUseSampleData(): boolean {
    const isDevelopment = env.NODE_ENV === 'development';
    const forceUseSamples = env.USE_SAMPLE_NOTIFICATIONS === 'true';
    return isDevelopment && forceUseSamples;
  }

  // Get notifications from Firebase
  async getNotifications(options: Partial<NotificationQueryOptions> = {}): Promise<ModernNotification[]> {
    const userId = this.getUserId();
    
    if (!userId) {
      console.warn('NotificationService: No user ID found');
      
      // Only show sample data in development mode with explicit flag
      if (this.shouldUseSampleData()) {
        console.log('NotificationService: Using sample data (development mode)');
        return this.createSampleNotifications();
      }
      
      // In production or without flag, try to get cached data
      const cached = this.getStoredNotifications();
      if (cached.length > 0) {
        console.log('NotificationService: Using cached notifications');
        return cached;
      }
      
      // No user ID, no cached data - return empty
      console.log('NotificationService: No user ID and no cached data, returning empty');
      return [];
    }

    console.log('NotificationService: Fetching notifications from Firebase for user:', userId);

    try {
      const notifications = await notificationRepository.getNotifications({
        userId,
        limit: options.limit || 50,
        hoursAgo: options.hoursAgo || 24,
        ...options
      });

      console.log('NotificationService: Retrieved', notifications.length, 'notifications from Firebase');

      // Cache in localStorage for offline access
      if (notifications.length > 0) {
        this.saveNotifications(notifications);
      }
      
      return notifications;
    } catch (error) {
      console.error('NotificationService: Error fetching notifications from Firebase:', error);
      
      // Try to return cached data
      const cached = this.getStoredNotifications();
      if (cached.length > 0) {
        console.log('NotificationService: Using cached notifications after Firebase error');
        return cached;
      }

      // If in development mode with flag, fall back to samples
      if (this.shouldUseSampleData()) {
        console.log('NotificationService: Using sample data after Firebase error (development mode)');
        return this.createSampleNotifications();
      }
      
      // In production, return empty array on error
      console.log('NotificationService: Returning empty array after Firebase error');
      return [];
    }
  }

  // Get notifications from localStorage (fallback)
  getStoredNotifications(): ModernNotification[] {
    const legacyNotifications: Notification[] = getItemLC(LCName.Notification) || [];
    return legacyNotifications.map(notif => this.convertLegacyNotification(notif));
  }

  // Save notifications to localStorage
  saveNotifications(notifications: ModernNotification[]): void {
    // For now, we'll maintain the legacy format in localStorage
    // In the future, this could be updated to use the modern format
    setItemLC('ModernNotifications', notifications);
    
    // Also trigger the legacy storage event for compatibility
    window.dispatchEvent(new Event('storageNotif'));
  }

  // Create sample notifications for demo (based on design)
  createSampleNotifications(): ModernNotification[] {
    return [
      {
        id: 'sample-1',
        ...createOrderNotification('ORDER_NEW', 'SHOPEE-12345', 'New order received - 5 items'),
        timestamp: new Date(Date.now() - 2 * 60 * 1000), // 2 minutes ago
      },
      {
        id: 'sample-2',
        ...createSyncNotification('Lazada Official Store'),
        timestamp: new Date(Date.now() - 60 * 60 * 1000), // 1 hour ago
      },
      {
        id: 'sample-3',
        ...createNotification('SYSTEM_ALERT', 'Low Stock Alert', 'Wireless Pro Headphones - only 2 left in stock'),
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
      }
    ];
  }

  // Mark notification as read
  async markAsRead(notificationId: string, notifications: ModernNotification[]): Promise<ModernNotification[]> {
    const userId = this.getUserId();
    
    // Update in Firebase if user ID is available
    if (userId) {
      try {
        await notificationRepository.markAsRead(userId, notificationId);
      } catch (error) {
        console.error('Error marking notification as read in Firebase:', error);
      }
    }

    // Update local state
    const updatedNotifications = notifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, read: true }
        : notification
    );

    // Save to localStorage
    this.saveNotifications(updatedNotifications);
    
    return updatedNotifications;
  }

  // Mark all notifications as read
  async markAllAsRead(notifications: ModernNotification[]): Promise<ModernNotification[]> {
    const userId = this.getUserId();
    
    // Update in Firebase if user ID is available
    if (userId) {
      try {
        await notificationRepository.markAllAsRead(userId);
      } catch (error) {
        console.error('Error marking all notifications as read in Firebase:', error);
      }
    }

    // Update local state
    const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));

    // Save to localStorage
    this.saveNotifications(updatedNotifications);
    
    return updatedNotifications;
  }

  // Delete notification
  async deleteNotification(notificationId: string, notifications: ModernNotification[]): Promise<ModernNotification[]> {
    const userId = this.getUserId();
    
    // Delete from Firebase if user ID is available
    if (userId) {
      try {
        await notificationRepository.deleteNotification(userId, notificationId);
      } catch (error) {
        console.error('Error deleting notification from Firebase:', error);
      }
    }

    // Update local state
    const updatedNotifications = notifications.filter(notification => notification.id !== notificationId);

    // Save to localStorage
    this.saveNotifications(updatedNotifications);
    
    return updatedNotifications;
  }

  // Delete all notifications
  deleteAllNotifications(): ModernNotification[] {
    return [];
  }

  // Get unread count
  getUnreadCount(notifications: ModernNotification[]): number {
    return notifications.filter(notification => !notification.read).length;
  }

  // Subscribe to real-time notifications from Firebase
  subscribeToNotifications(
    onUpdate: (notifications: ModernNotification[]) => void,
    options: Partial<NotificationQueryOptions> = {}
  ): (() => void) | null {
    const userId = this.getUserId();
    if (!userId) {
      console.warn('No user ID found, cannot subscribe to real-time notifications');
      return null;
    }

    return notificationRepository.subscribeToNotifications(
      userId,
      (newNotifications) => {
        // Merge with existing cached notifications and remove duplicates
        const cached = getItemLC('ModernNotifications') || [];
        const merged = [...newNotifications, ...cached];
        const unique = merged.reduce((acc, current) => {
          const exists = acc.find(item => item.id === current.id);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, [] as ModernNotification[]);

        // Sort by timestamp
        const sorted = unique.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        // Cache updated notifications
        this.saveNotifications(sorted);
        
        // Call update callback
        onUpdate(sorted);
      },
      { userId, ...options }
    );
  }
}

export const notificationService = NotificationService.getInstance();